import { CustomFlyoutDrawerState } from "@electricMotorShop/Store/Components/FlyoutDrawer/FlyoutDrawerReducer";
import setDrawerIsOpen from "@electricMotorShop/Store/Components/FlyoutDrawer/Handlers/SetDrawerIsOpen";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { updateCart } from "@insite/client-framework/Services/CartService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import loadWarehouses from "@insite/client-framework/Store/Components/FindLocationModal/Handlers/LoadWarehouses";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import changeCustomerContext from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocationPathAndQuery } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWarehousesDataView } from "@insite/client-framework/Store/Data/Warehouses/WarehousesSelectors";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import MapPin from "@insite/mobius/Icons/MapPin";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

interface OwnProps extends WidgetProps {
    theme: BaseTheme;
}

const mapStateToProps = (state: CustomFlyoutDrawerState) => {
    const drawerState = state.xcentiumFlyoutDrawer.locationLink;
    return {
        drawerState,
        warehousesDataView: getWarehousesDataView(state, state.components.findLocationModal.getWarehousesParameter),
        session: state.context.session,
        settings: getSettingsCollection(state),
        cart: getCurrentCartState(state).value,
        state,
    };
};

const mapDispatchToProps = {
    setDrawerIsOpen,
    loadWarehouses,
    updatePickUpWarehouse,
    changeCustomerContext,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const baseDrawerStyles: DrawerPresentationProps = {
    closeButtonProps: {
        shape: "rectangle",
        buttonType: "solid",
        color: "common.background",
        size: 50,
        shadow: false,
        hoverMode: "none",
    },
    closeButtonIconProps: {
        src: "X",
        size: 24,
    },
    cssOverrides: {
        drawerContent: css`
            margin: 10px 20px;
            overflow-y: inherit;
        `,
        drawerBody: css`
            background: ${getColor("common.background")};
            height: auto;
            position: absolute;
            overflow: inherit;
        `,
        drawerTitle: css`
            background: ${getColor("common.background")};
            padding: 0;
            position: absolute;
            right: 0;
            z-index: 1;
            border-radius: 4px;
        `,
    },
    position: "top",
};

export interface LocationLinkStyles {
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    linkContentWrapper?: InjectableCss;
    linkIcon?: IconPresentationProps;
    linkText?: TypographyPresentationProps;

    warehousesWrapper?: InjectableCss;
    warehouseWrapper?: InjectableCss;
    warehouseInnerWrapper?: InjectableCss;
    warehouseTitle?: TypographyPresentationProps;
    warehouseAddress?: TypographyPresentationProps;
    warehousePhone?: TypographyPresentationProps;
    hoursWrapper?: InjectableCss;
    hours?: TypographyPresentationProps;
    warehouseOpen?: TypographyPresentationProps;
    warehouseClosed?: TypographyPresentationProps;
    warehouseDefault?: TypographyPresentationProps;

    myWarehouseButton?: ButtonPresentationProps;
    myWarehouseButtonText?: TypographyPresentationProps;
    setWarehouseButton?: ButtonPresentationProps;
    setWarehouseButtonText?: TypographyPresentationProps;
    hr?: InjectableCss;
}

const locationLinkStyles: LocationLinkStyles = {
    linkWrapper: {
        css: css`
            display: flex;
        `,
    },
    link: {
        css: css`
            position: relative;
            cursor: pointer;
        `,
    },
    linkContentWrapper: {
        css: css`
            width: 100%;
            display: flex;
            height: 50px;
            flex-direction: row;
            flex-wrap: nowrap;
            justify-content: flex-start;
            align-items: center;
        `,
    },
    linkIcon: {
        color: "common.background",
    },
    linkText: {
        css: css`
            margin-left: 8px;
            color: ${getColor("common.background")};
            &:hover {
                text-decoration: underline;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    warehousesWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    warehouseWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    warehouseTitle: {
        css: css`
            width: 100%;
            display: block;
            font-weight: bold;
            font-size: 18px;
        `,
    },
    warehouseAddress: {
        css: css`
            width: 100%;
            display: block;
        `,
    },
    warehousePhone: {
        css: css`
            width: 100%;
            display: block;
            margin-bottom: 8px;
        `,
    },
    hoursWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: row;
            margin-bottom: 8px;
        `,
    },
    hours: {
        css: css`
            padding: 0 4px;
        `,
    },
    warehouseOpen: {
        css: css`
            color: ${getColor("success.main")};
            font-weight: bold;
        `,
    },
    warehouseClosed: {
        css: css`
            color: ${getColor("danger.main")};
            font-weight: bold;
        `,
    },
    warehouseDefault: {
        css: css`
            color: ${getColor("text.disabled")};
            font-weight: bold;
        `,
    },
    myWarehouseButton: {
        css: css`
            display: block;
            width: 45%;
            margin-bottom: 12px;
            box-shadow: none !important;
            border-radius: 4px;
        `,
    },
    myWarehouseButtonText: {
        css: css`
            font-weight: bold;
        `,
    },
    setWarehouseButton: {
        css: css`
            display: block;
            width: 45%;
            margin-bottom: 12px;
            background-color: ${getColor("primary.accent")};
            box-shadow: none !important;
            border-radius: 4px;
        `,
    },
    setWarehouseButtonText: {
        css: css`
            font-weight: bold;
        `,
    },
};

export const LocationLink: React.FC<Props> = ({
    drawerState,
    warehousesDataView,
    session,
    cart,
    state,
    setDrawerIsOpen,
    loadWarehouses,
    changeCustomerContext,
    theme,
}) => {
    const [warehouses, setWarehouses] = useState<WarehouseModel[]>([]);

    const isDrawerOpen = drawerState.flyoutDrawerIsOpen;
    const styles = locationLinkStyles;
    const currentPickUpWarehouse = session.pickUpWarehouse;

    useEffect(() => {
        if (!warehousesDataView.value && !warehousesDataView.isLoading) {
            loadWarehouses({
                search: "",
                latitude: 0.0,
                longitude: 0.0,
                radius: 50000,
                onlyPickupWarehouses: false,
                excludeCurrentPickupWarehouse: false,
            });
        }
    }, []);

    useEffect(() => {
        if (!warehousesDataView.isLoading && warehousesDataView.value) {
            setWarehouses(warehousesDataView.value);
        }
    }, [warehousesDataView]);

    const handleCloseDrawer = () => {
        setDrawerIsOpen({ drawer: "LocationLink", isOpen: false });
    };

    let showDrawerInterval: NodeJS.Timeout;

    // Function to handle click on icon.
    const handleLocationLinkClick = (event: any) => {
        clearInterval(showDrawerInterval);
        let opened = false;

        showDrawerInterval = setInterval(() => {
            if (!opened) {
                positionDrawer(event);
                setDrawerIsOpen({ drawer: "LocationLink", isOpen: true });
                opened = true;
                const rect = event.target.closest(".link-container")?.getBoundingClientRect();
            } else {
                clearInterval(showDrawerInterval);
            }
        }, 0);
    };

    const positionDrawer = (event: any) => {
        // Getting parent
        const rect = event.target.closest(".link-container")?.getBoundingClientRect();

        if (rect && typeof window !== "undefined") {
            // set to the width of the drawer
            const drawerWidth = 550;

            // get location to put drawer
            const right = window.innerWidth - rect.right - 16;
            const bottom = rect.bottom + 20;

            // get location for arrow
            const arrowWidth = 22;
            const arrowRight = (rect.width / 2) - arrowWidth;
            const arrowRightMobile = right - (rect.width / 2);

            // set the style
            if (baseDrawerStyles?.cssOverrides?.drawerBody) {
                if (window.innerWidth < theme.breakpoints.values[3]) {
                    baseDrawerStyles.cssOverrides.drawerBody = css`
                        background: ${getColor("common.background")};
                        height: auto;
                        position: fixed;
                        top: ${bottom}px;
                        width: ${window.innerWidth}px;
                        overflow: initial;
                        border-radius: 4px;
                        :before {
                            content: "";
                            border-left: ${arrowWidth}px solid transparent;
                            border-right: ${arrowWidth}px solid transparent;
                            border-bottom: ${arrowWidth}px solid ${getColor("common.background")};
                            top: -${arrowWidth}px;
                            right: ${arrowRightMobile}px;
                            position: absolute;
                        }
                    `;
                } else {
                    baseDrawerStyles.cssOverrides.drawerBody = css`
                        background: ${getColor("common.background")};
                        height: auto;
                        position: absolute;
                        overflow-x: inherit;
                        overflow-y: inherit;
                        top: ${bottom}px;
                        right: ${right}px;
                        max-width: 550px;
                        border-radius: 4px;
                        :before {
                            content: "";
                            border-left: ${arrowWidth}px solid transparent;
                            border-right: ${arrowWidth}px solid transparent;
                            border-bottom: ${arrowWidth}px solid ${getColor("common.background")};
                            top: -${arrowWidth}px;
                            right: ${arrowRight}px;
                            position: absolute;
                        }
                    `;
                }
            }
        }
    };

    const handleSetWarehouseButtonClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        warehouse: WarehouseModel,
    ) => {
        if (cart) {
            changeCustomerContext({
                fulfillmentMethod: FulfillmentMethod.PickUp,
                pickUpWarehouse: warehouse,
                returnUrl: getLocationPathAndQuery(state),
                billToId: session?.billToId,
                shipToId: session?.shipToId,
                isDefault: true
            });
        }
    };

    const determineHours = (hours: string): string => {
        const currentDate = new Date();
        const dayOfWeek = currentDate.getDay();
        let todayHours = "";

        switch (dayOfWeek) {
            case 0:
                todayHours = determineHoursForDay("[Su]", hours);
                break;
            case 1:
                todayHours = determineHoursForDay("[M]", hours);
                break;
            case 2:
                todayHours = determineHoursForDay("[Tu]", hours);
                break;
            case 3:
                todayHours = determineHoursForDay("[W]", hours);
                break;
            case 4:
                todayHours = determineHoursForDay("[Th]", hours);
                break;
            case 5:
                todayHours = determineHoursForDay("[F]", hours);
                break;
            case 6:
                todayHours = determineHoursForDay("[Sa]", hours);
                break;
            default:
                break;
        }

        return todayHours;
    };

    const determineHoursForDay = (dayCode: string, hours: string): string => {
        const splitHours = hours.split("\n");
        const findHours = splitHours.find(x => x.startsWith(dayCode));
        if (findHours && findHours.length > 0) {
            return findHours.replace(dayCode, "").replace(";", "");
        }
        return "unknown";
    };

    const formatWarehouseHours = (warehouse: WarehouseModel) => {
        const todaysHours = determineHours(warehouse.hours);
        const isWarehouseOpen = !["unknown", "closed"].includes(todaysHours.toLowerCase());

        return (
            <StyledWrapper {...styles.hoursWrapper}>
                <Typography {...styles.hours}>Today:</Typography>
                {isWarehouseOpen ? (
                    <Typography {...styles.warehouseOpen}>Open</Typography>
                ) : (
                    <Typography {...styles.warehouseClosed}>Closed</Typography>
                )}
                <Typography {...styles.hours}>{todaysHours}</Typography>
            </StyledWrapper>
        );
    };

    const formatWarehouseButton = (warehouse: WarehouseModel) => {
        const isCurrentPickUpWarehouse = warehouse.id === currentPickUpWarehouse?.id || false;

        if (isCurrentPickUpWarehouse) {
            return (
                <Button {...styles.myWarehouseButton} disabled={true}>
                    <Typography {...styles.myWarehouseButtonText}>My Location</Typography>
                </Button>
            );
        }

        return (
            <Button {...styles.setWarehouseButton} onClick={e => handleSetWarehouseButtonClick(e, warehouse)}>
                <Typography {...styles.setWarehouseButtonText}>Set As My Location</Typography>
            </Button>
        );
    };

    return (
        <>
            <StyledWrapper {...styles.linkWrapper} data-test-selector="LocationLink_linkWrapper">
                <Link
                    {...styles.link}
                    onClick={handleLocationLinkClick}
                    data-test-selector="LocationLink_link"
                    id="locationLink"
                >
                    <StyledWrapper {...styles.linkContentWrapper} data-test-selector="LocationLink_linkContentWrapper">
                        <Icon {...styles.linkIcon} src={MapPin} data-test-selector="LocationLink_linkIcon" />
                        <Typography {...styles.linkText} data-test-selector="LocationLink_linkText">
                            {currentPickUpWarehouse ? currentPickUpWarehouse.description : translate("Location")}
                        </Typography>
                    </StyledWrapper>
                </Link>
            </StyledWrapper>

            <Drawer
                {...baseDrawerStyles}
                isOpen={isDrawerOpen}
                handleClose={handleCloseDrawer}
                data-test-selector="locationDrawer"
            >
                <StyledWrapper {...styles.warehousesWrapper}>
                    {warehouses &&
                        warehouses.map((warehouse, index) => {
                            const warehouseAddress = `${warehouse.address1.trim()}, ${warehouse.city.trim()} ${
                                warehouse.state
                            } ${warehouse.postalCode}`;
                            const warehousePhone =
                                warehouse.phone && warehouse.phone.length > 0 ? warehouse.phone : "000 000-0000";
                            const warehouseHours = formatWarehouseHours(warehouse);
                            const warehouseButton = formatWarehouseButton(warehouse);

                            return (
                                <StyledWrapper {...styles.warehouseWrapper} key={index}>
                                    <StyledWrapper {...styles.warehouseInnerWrapper}>
                                        <Typography {...styles.warehouseTitle}>{warehouse.description}</Typography>
                                        <Typography {...styles.warehouseAddress}>{warehouseAddress}</Typography>
                                        <Typography {...styles.warehousePhone}>{warehousePhone}</Typography>
                                        {warehouseHours}
                                        {warehouseButton}
                                        {index < warehouses.length - 1 && <hr />}
                                    </StyledWrapper>
                                </StyledWrapper>
                            );
                        })}
                </StyledWrapper>
            </Drawer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(LocationLink)),
    definition: {
        displayName: "Header Location Link",
        icon: "arrow-right-to-bracket",
        group: "Common",
        fieldDefinitions: [],
    },
};

export default widgetModule;
